import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import moment from "moment";

export const downloadSalesExcel = (data, fromdate, toDate) => {
  // Helper function to generate dynamic Excel filename for sales
  const getSalesExcelFilename = (fromDate, toDate) => {
    if (!fromDate || !toDate) {
      return "sales_report.xlsx";
    }
    const start = moment(fromDate, "YYYY-MM-DD");
    const end = moment(toDate, "YYYY-MM-DD");
    const startMonth = start.format("MMM").toLowerCase();
    const endMonth = end.format("MMM").toLowerCase();
    if (start.isSame(end, "month")) {
      return `sales_${startMonth}.xlsx`;
    } else {
      return `sales_${startMonth}_to_${endMonth}.xlsx`;
    }
  };

  const salesExcelFileName = getSalesExcelFilename(fromdate, toDate);

  // Enforce ASCENDING date sequencing
  const sortedData = [...data].sort(
    (a, b) => new Date(a.bill_date) - new Date(b.bill_date)
  );

  const processedData = sortedData.map((item, index) => {
    const gst = Array.isArray(item.product)
      ? item.product
          .reduce((acc, product) => acc + parseFloat(product.tax_amt || 0), 0)
          .toFixed(2)
      : 0;
    const balance = parseFloat(item.total || 0) - parseFloat(item.paid || 0);

    // Process payment methods
    const paymentMethod = (() => {
      try {
        // Ensure item.payment_method is parsed into an array
        const paymentArray = Array.isArray(item.payment_method)
          ? item.payment_method
          : typeof item.payment_method === "string"
          ? JSON.parse(item.payment_method) // Parse the JSON string
          : [];

        // Process the array
        return paymentArray.length > 0
          ? paymentArray
              .map((pm) => {
                const method = pm.payment_method || "Unknown";
                const amount = pm.amount || "0";
                return `${method} - ${amount}`;
              })
              .join(", ")
          : " ";
      } catch (error) {
        return " "; // Return N/A if parsing fails
      }
    })();

    return {
      "S No": index + 1,
      Date: item.bill_date || " ",
      "Receipt No": item.bill_no || " ",
      "Party Name": item.party_name || " ",
      "GST Number": item.party_details?.gst_no || " ",
      Product: Array.isArray(item.product)
        ? item.product.map((p) => p.product_name).join(", ")
        : "",
      Quantity: Array.isArray(item.product)
        ? item.product.map((p) => p.qty).join(", ")
        : "0",
      Price: Array.isArray(item.product)
        ? item.product.map((p) => p.price_unit).join(", ")
        : "0",
      SubTotal: parseFloat(item.sum_total || 0).toFixed(2),
      "Tax Amount": item.gst_amount || 0,
      Discount: parseFloat(item.discount_amount || 0).toFixed(2),
      "Grand Total": parseFloat(item.total || 0).toFixed(2),
      // Paid: parseFloat(item.paid || 0).toFixed(2),
      // Balance: balance.toFixed(2),
      // "Payment Method": paymentMethod, // Updated
      Remark: item.remark || " ",
    };
  });

  // Calculate summary values
  const subTotal = sortedData.reduce(
    (acc, item) => acc + parseFloat(item.sum_total || 0),
    0
  );
  const subGrandTotal = sortedData.reduce(
    (acc, item) => acc + parseFloat(item.total || 0),
    0
  );
  const discount = sortedData.reduce(
    (acc, item) => acc + parseFloat(item.discount_amount || 0),
    0
  );
  const totalTaxAmount = sortedData.reduce(
    (acc, item) => acc + parseFloat(item.gst_amount || 0),
    0
  );

  // Add summary row
  processedData.push({
    "S No": "Total",
    Date: "",
    "Receipt No": "",
    "Party Name": "",
    "GST Number": "",
    Product: "",
    Quantity: "",
    Price: "",
    SubTotal: subTotal.toFixed(2),
    "Tax Amount": totalTaxAmount.toFixed(2),
    Discount: discount.toFixed(2),
    "Grand Total": subGrandTotal.toFixed(2),
    // Paid: "", // Optional: Can calculate total paid if required
    // Balance: "", // Optional: Can calculate total balance if required
    // "Payment Method": "",
  });

  // Create workbook and worksheet
  const worksheet = XLSX.utils.json_to_sheet(processedData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Report");

  // Add metadata
  XLSX.utils.sheet_add_aoa(
    worksheet,
    [[`Sales Report: ${fromdate} to ${toDate}`], []],
    { origin: "A1" }
  );

  // Adjust column widths
  const colWidths = [
    { wpx: 50 }, // S No
    { wpx: 100 }, // Date
    { wpx: 120 }, // Receipt No
    { wpx: 200 }, // Party Name
    { wpx: 150 }, // GST Number
    { wpx: 200 }, // Product
    { wpx: 80 }, // Quantity
    { wpx: 80 }, // Price
    { wpx: 100 }, // SubTotal
    { wpx: 120 }, //  Tax Amount
    { wpx: 100 }, // Discount
    { wpx: 120 }, // Grand Total
    { wpx: 100 }, // Paid
    { wpx: 100 }, // Balance
    { wpx: 150 }, // Payment Method
    { wpx: 150 }, // Payment Method
  ];
  worksheet["!cols"] = colWidths;

  // Convert to Excel file and trigger download
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(blob, salesExcelFileName);
};
